import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, DatePicker, Tabs, Select, Spin, Checkbox } from 'antd';
import dayjs from 'dayjs';
import { SignaturePad } from '@/components/signature-pad';
import { API } from '@/services/api.service';
import { useNotify } from '@/hooks';
import { ArrowIcon } from '@/assets/icons';
import { SimpleTable } from '@/components/simple-table';
import { useToggle } from '@/hooks';
import { cn } from '@/libs/utils';
import {
  COMPANY_OWNERSHIP_STRUCTURE_FORM,
  OWNERS_TYPE_OPTIONS,
  USER_TYPES_OPTIONS,
} from '@/shared/company';
import {
  DATE_FORMATS,
  formatDateForBackend,
  parseDateForInput,
} from '@/utils/formUtils';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import { translateOptions } from '@/utils/utils';
import OwnersColumns from '../table/owners-table/OwnersColumns';

export default function OwnershipStructureForm({
  formData,
  className,
  onClose,
  onScroll,
  onSubmit,
  companyId,
}) {
  const { t } = useTranslation(['forms', 'tables', 'messages']);
  const [form] = Form.useForm();
  const notification = useNotify();

  const [editingIndex, setEditingIndex] = useState(null);
  const [ownershipStructure, setOwnershipStructure] = useState([]);
  const [signatureUploading, setSignatureUploading] = useState(false);
  // Local signature state for current editing person (newly uploaded)
  const [currentSignature, setCurrentSignature] = useState(null);
  // Presigned URL for displaying existing signature
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);

  useEffect(() => {
    if (formData?.length > 0) {
      setOwnershipStructure(formData);
    }
  }, [formData]);

  const activeKey = Form.useWatch('type', form);
  const hasAuthorizedType = activeKey === OWNERS_TYPE_OPTIONS.AUTHORIZED;

  // Check if we're editing an existing authorized person
  const isEditingExistingAuthorized =
    editingIndex !== null &&
    ownershipStructure[editingIndex]?.type === OWNERS_TYPE_OPTIONS.AUTHORIZED;

  // Get current editing person's signature
  const editingPersonSignature = isEditingExistingAuthorized
    ? ownershipStructure[editingIndex]?.signature
    : null;

  // Fetch presigned URL when editing person with existing signature
  useEffect(() => {
    const fetchSignatureUrl = async () => {
      if (editingPersonSignature?.key && !currentSignature) {
        try {
          const { url } = await API.files.downloadFile({ key: editingPersonSignature.key });
          setSignaturePreviewUrl(url);
        } catch (error) {
          console.error('Failed to fetch signature URL:', error);
          setSignaturePreviewUrl(null);
        }
      } else if (!editingPersonSignature) {
        setSignaturePreviewUrl(null);
      }
    };
    fetchSignatureUrl();
  }, [editingPersonSignature, currentSignature]);

  // Signature handlers
  const handleSignatureSave = useCallback(
    async (base64Data) => {
      if (!companyId) {
        notification.warning({
          message: t('warning', { ns: 'messages' }),
          description: t('saveCompanyFirst', { ns: 'messages' }),
        });
        return;
      }

      setSignatureUploading(true);
      try {
        // If editing existing authorized with signature, pass the key to delete old one
        const existingKey = editingPersonSignature?.key;

        const response = await API.files.uploadSignature({
          signatureData: base64Data,
          companyId,
          existingSignatureKey: existingKey,
        });

        const newSignature = {
          typeOfFile: response.data?.typeOfFile,
          location: response.data?.location,
          originalName: response.data?.originalName,
          key: response.data?.key,
          // Keep base64 for immediate display (S3 URL needs presigned)
          previewUrl: base64Data,
        };

        setCurrentSignature(newSignature);

        notification.success({
          message: t('success', { ns: 'messages' }),
          description: t('signatureUploaded', { ns: 'messages' }),
        });
      } catch (error) {
        console.error('Upload error:', error);
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: error.message,
        });
      } finally {
        setSignatureUploading(false);
      }
    },
    [companyId, notification, t, editingPersonSignature]
  );

  const handleSignatureClear = useCallback(async () => {
    const existingKey = currentSignature?.key || editingPersonSignature?.key;
    if (existingKey) {
      try {
        await API.files.deleteFile({ key: existingKey });
      } catch (error) {
        console.error('Failed to delete signature from S3:', error);
      }
    }
    // Clear all signature states
    setCurrentSignature(null);
    setSignaturePreviewUrl(null);

    // Update ownershipStructure to remove signature from current person
    if (editingIndex !== null) {
      setOwnershipStructure((prev) => {
        const updated = [...prev];
        if (updated[editingIndex]) {
          updated[editingIndex] = {
            ...updated[editingIndex],
            signature: null,
          };
        }
        return updated;
      });
    }
  }, [currentSignature, editingPersonSignature, editingIndex]);

  const handleReset = () => {
    form.resetFields();
    // * Reset all values from table
    setOwnershipStructure(formData || []);
    // * Close sidebar after reset
    onClose();
    // * Reset scroll to top
    onScroll();
  };

  const handleFieldReset = (formValues) => {
    form.resetFields(Object.keys(formValues).filter((type) => type !== 'type'));
    setCurrentSignature(null);
    setSignaturePreviewUrl(null);
  };

  const onFinish = (values) => {
    const formValues = { ...values };
    const isAuthorized = formValues.type === OWNERS_TYPE_OPTIONS.AUTHORIZED;

    const formattedValues = {
      ...formValues,
      dateOfBirth: formatDateForBackend(formValues?.dateOfBirth),
      dateOfIdDocIssue: formatDateForBackend(formValues?.dateOfIdDocIssue),
      // Include signature data for authorized persons
      signature: isAuthorized ? (currentSignature || editingPersonSignature) : null,
      useSignatureOnDocs: isAuthorized ? (formValues.useSignatureOnDocs || false) : false,
    };

    setOwnershipStructure((prev) => {
      let updated;
      if (editingIndex !== null) {
        updated = [...prev];
        updated[editingIndex] = formattedValues;
      } else {
        updated = [...prev, formattedValues];
      }

      // If this authorized person has useSignatureOnDocs=true, set all others to false
      if (isAuthorized && formattedValues.useSignatureOnDocs) {
        updated = updated.map((person, idx) => {
          const currentIdx = editingIndex !== null ? editingIndex : updated.length - 1;
          if (person.type === OWNERS_TYPE_OPTIONS.AUTHORIZED && idx !== currentIdx) {
            return { ...person, useSignatureOnDocs: false };
          }
          return person;
        });
      }

      return updated;
    });

    setEditingIndex(null);
    handleFieldReset(formValues);
    onScroll();
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setCurrentSignature(null);
    setSignaturePreviewUrl(null);
    form.resetFields(
      Object.keys(COMPANY_OWNERSHIP_STRUCTURE_FORM).filter((key) => key !== 'type')
    );
    onScroll();
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    // Reset current signature - useEffect will fetch presigned URL for existing
    setCurrentSignature(null);
    setSignaturePreviewUrl(null);

    const ownerToEdit = ownershipStructure[index];

    form.setFieldsValue({
      ...ownerToEdit,
      dateOfBirth: parseDateForInput(
        ownerToEdit?.dateOfBirth,
        DATE_FORMATS.STANDARD
      ),
      dateOfIdDocIssue: parseDateForInput(
        ownerToEdit?.dateOfIdDocIssue,
        DATE_FORMATS.STANDARD
      ),
    });
  };

  const handleRemove = async (index) => {
    const personToRemove = ownershipStructure[index];

    // Delete signature from S3 if exists
    if (personToRemove?.signature?.key) {
      try {
        await API.files.deleteFile({ key: personToRemove.signature.key });
      } catch (error) {
        console.error('Failed to delete signature from S3:', error);
      }
    }

    setEditingIndex(null);
    setCurrentSignature(null);
    setOwnershipStructure((prev) => {
      return prev.filter((_, idx) => idx !== index);
    });

    form.resetFields();
  };

  const getOwnersType = (ownerType) => {
    return ownershipStructure.filter(({ type }) => type === ownerType);
  };

  const groupedOwners = {
    [t('founder', { ns: 'tables' })]: getOwnersType(
      OWNERS_TYPE_OPTIONS.FOUNDER
    ),
    [t('owner', { ns: 'tables' })]: getOwnersType(OWNERS_TYPE_OPTIONS.OWNER),
    [t('authorized', { ns: 'tables' })]: getOwnersType(
      OWNERS_TYPE_OPTIONS.AUTHORIZED
    ),
  };

  const handleSubmit = () => {
    const payload = {
      ownershipStructure: ownershipStructure,
    };

    onSubmit(payload);

    onClose();
  };

  return (
    <Form
      form={form}
      name="company_ownership_structure_form"
      layout="vertical"
      initialValues={{
        ...COMPANY_OWNERSHIP_STRUCTURE_FORM,
      }}
      autoComplete="off"
      className={cn('form p-4', className)}
      onFinish={onFinish}
    >
      <OwnershipResults
        groupedOwners={groupedOwners}
        ownershipStructure={ownershipStructure}
        handleEdit={handleEdit}
        handleRemove={handleRemove}
      />

      <div className="sticky left-0 right-0 top-0 z-[1] bg-white">
        <Form.Item label={null} name="type">
          <Tabs
            activeKey={activeKey}
            items={translateOptions(USER_TYPES_OPTIONS, t, 'label', 'tables')}
            tabBarStyle={{ marginBottom: 0 }}
          />
        </Form.Item>
      </div>

      <Form.Item
        name="name"
        label={t('ownershipName', { ns: 'forms' })}
        rules={[
          {
            required: true,
            message: t('formValidation.required', { ns: 'forms' }),
          },
        ]}
      >
        <Input
          placeholder={t('ownershipName', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item name="dateOfBirth" label={t('dateOfBirth', { ns: 'forms' })}>
        <DatePicker
          className="w-full"
          format={DATE_FORMATS.DISPLAY}
          placeholder={t('dateOfBirth', { ns: 'forms' })}
          defaultPickerValue={dayjs('1980-01-01')}
        />
      </Form.Item>

      <Form.Item name="placeOfBirth" label={t('placeOfBirth', { ns: 'forms' })}>
        <Input placeholder={t('placeOfBirth', { ns: 'forms' })} />
      </Form.Item>

      <Form.Item
        name="countryOfBirth"
        label={t('countryOfBirth', { ns: 'forms' })}
      >
        <Input placeholder={t('countryOfBirth', { ns: 'forms' })} />
      </Form.Item>

      <Form.Item
        name="addressOfResidence"
        label={t('addressOfResidence', { ns: 'forms' })}
      >
        <Input
          placeholder={t('addressOfResidence', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item
        name="placeOfResidence"
        label={t('placeOfResidence', { ns: 'forms' })}
      >
        <Input placeholder={t('placeOfResidence', { ns: 'forms' })} />
      </Form.Item>

      <Form.Item
        name="countryOfResidence"
        label={t('countryOfResidence', { ns: 'forms' })}
      >
        <Input
          placeholder={t('countryOfResidence', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item
        name="JMBG"
        label={t('JMBG', {
          ns: 'forms',
        })}
        rules={[
          {
            required: true,
            message: t('formValidation.required', {
              ns: 'forms',
            }),
          },
        ]}
      >
        <Input
          min={0}
          type="number"
          placeholder={t('JMBG', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item
        name="typeOfDocument"
        label={t('typeOfDocument', { ns: 'forms' })}
      >
        <Select
          allowClear
          placeholder={t('typeOfDocument', {
            ns: 'forms',
          })}
          className="w-full"
          loading={false}
          options={[
            {
              value: 'Lična karta',
              label: 'Personal ID / Lična karta',
            },
            {
              value: 'Pasoš',
              label: 'Passport / Pasoš',
            },
          ]}
        />
      </Form.Item>

      <Form.Item
        name="numberOfDocument"
        label={t('numberOfDocument', {
          ns: 'forms',
        })}
        rules={[
          {
            required: true,
            message: t('formValidation.required', {
              ns: 'forms',
            }),
          },
        ]}
      >
        <Input
          min={0}
          type="number"
          placeholder={t('numberOfDocument', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item
        name="issuerOfDocument"
        label={t('issuerOfDocument', { ns: 'forms' })}
      >
        <Input
          placeholder={t('issuerOfDocument', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item
        name="placeOfIdDocIssue"
        label={t('placeOfIdDocIssue', {
          ns: 'forms',
        })}
      >
        <Input
          placeholder={t('placeOfIdDocIssue', {
            ns: 'forms',
          })}
        />
      </Form.Item>

      <Form.Item
        name="dateOfIdDocIssue"
        label={t('dateOfIdDocIssue', {
          ns: 'forms',
        })}
      >
        <DatePicker
          className="w-full"
          format={DATE_FORMATS.DISPLAY}
          placeholder={t('dateOfIdDocIssue', { ns: 'forms' })}
        />
      </Form.Item>

      {!hasAuthorizedType && (
        <Form.Item
          name="percentageOfOwnership"
          label={t('percentageOfOwnership', {
            ns: 'forms',
          })}
          rules={[
            {
              required: true,
              message: t('formValidation.numberField.invalid', { ns: 'forms' }),
            },
          ]}
        >
          <Input
            min={0}
            max={100}
            prefix={<span className="mr-1 border-r pr-2.5">%</span>}
            placeholder={t('percentageOfOwnership', {
              ns: 'forms',
            })}
            onChange={(e) =>
              formValidateAndSetNumber(form, e, 'percentageOfOwnership', true)
            }
          />
        </Form.Item>
      )}

      {hasAuthorizedType && companyId && (
        <div className="mb-4 rounded border bg-zinc-50 p-4">
          <p className="mb-2 font-medium text-gray-700">
            {t('authorizedPersonSignature', { ns: 'forms' })}
          </p>
          <Spin spinning={signatureUploading}>
            <SignaturePad
              onSave={handleSignatureSave}
              onClear={handleSignatureClear}
              existingSignature={currentSignature?.previewUrl || signaturePreviewUrl}
            />
          </Spin>
          <Form.Item
            name="useSignatureOnDocs"
            valuePropName="checked"
            className="mb-0 mt-3"
          >
            <Checkbox>
              {t('useSignatureOnDocs', { ns: 'forms' })}
            </Checkbox>
          </Form.Item>
        </div>
      )}

      <div className="mb-5 flex justify-end gap-2">
        {editingIndex !== null && (
          <Button
            type="default"
            className="btn-default"
            onClick={handleCancel}
          >
            {t('addNew', { ns: 'forms' })}
          </Button>
        )}

        <Button
          type="default"
          htmlType="submit"
          disabled={false}
          className="btn-primary-outlined"
        >
          {t(editingIndex !== null ? 'save' : 'add', { ns: 'forms' })}
        </Button>
      </div>

      <div className="form-footer form-footer--sticky">
        <div className="flex gap-2">
          <Button
            type="default"
            disabled={false}
            className="btn-default btn-default--hover"
            onClick={handleReset}
          >
            {t('reset', { ns: 'forms' })}
          </Button>

          <Button type="primary" className="btn-primary" onClick={handleSubmit}>
            {t('submit', { ns: 'forms' })}
          </Button>
        </div>
      </div>
    </Form>
  );
}

function OwnershipResults({
  groupedOwners,
  ownershipStructure,
  handleEdit,
  handleRemove,
}) {
  return (
    <div className="mb-5 divide-y rounded-md border bg-zinc-100">
      {Object.entries(groupedOwners).map(([type, items], idx) => {
        return (
          <OwnershipResultItem
            key={idx}
            label={type}
            items={items}
            ownershipStructure={ownershipStructure}
            handleEdit={handleEdit}
            handleRemove={handleRemove}
          />
        );
      })}
    </div>
  );
}

function OwnershipResultItem({
  label,
  items,
  ownershipStructure,
  handleEdit,
  handleRemove,
}) {
  const { open: toggle, onToggle } = useToggle(items.length > 0 ? true : false);

  useEffect(() => {
    const isToggled = items?.length > 0 ? true : false;
    onToggle(isToggled);
  }, [items, onToggle]);

  const handleToggle = () => {
    onToggle(!toggle);
  };

  return (
    <div className="grid gap-2 p-2">
      <div className="flex items-center justify-between">
        <p className="float-start flex gap-2">
          <strong>{label}</strong>
          <span className="relative -top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full border-[1px] border-white bg-accent text-[12px] font-semibold text-white shadow-md">
            <span className="inline-block h-max leading-none">
              {items.length}
            </span>
          </span>
        </p>

        <Button
          type="default"
          className="btn-default float-right h-7 w-7 !rounded-none bg-transparent p-0 hover:!bg-transparent"
          onClick={handleToggle}
          icon={
            <ArrowIcon
              className={cn(
                '-rotate-90 transition-transform duration-150',
                toggle ? 'rotate-90' : ''
              )}
            />
          }
        >
          <span className="sr-only">Toggle</span>
        </Button>
      </div>

      <SimpleTable
        isLoading={false}
        data={items}
        className={cn('mb-0', toggle ? 'block' : 'hidden')}
        metadata={{
          noDataClassName: 'p-2 bg-white',
        }}
        columns={() =>
          OwnersColumns({
            owners: ownershipStructure,
            handleEdit,
            handleRemove,
          })
        }
      />
    </div>
  );
}
